import { useMutation } from '@apollo/client';
import {
  DefaultButton,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Stack,
} from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { AccountingEntryInvoice } from 'ap/signing/accountingEntry/view/__generated__/AccountingEntryInvoice';
import {
  InvoiceSigningAccountingUnStamper,
  InvoiceSigningAccountingUnStamperVariables,
} from 'ap/signing/accountingEntry/view/__generated__/InvoiceSigningAccountingUnStamper';
import { AccountingEntryValues } from 'ap/signing/accountingEntry/view/types';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { showCustomToast } from 'common/components/CustomToast/utils';
import { DialogWrapper } from 'common/components/DialogWrapper';
import { TABLE_ROWS } from 'common/constants';
import {
  InvoiceSigningAccountingUnstamperInput,
  InvoiceSigningApprovalRevokeInput,
} from 'common/types/globalTypes';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import {
  InvoiceSigningApprovalRevoke,
  InvoiceSigningApprovalRevokeVariables,
} from 'common/components/Modules/TransactionEdit/graphql/__generated__/InvoiceSigningApprovalRevoke';
import { useStyles } from './index.styles';

interface FooterActionsProps {
  invoiceDetailsData: AccountingEntryInvoice | undefined;
  batchTransactionId: string | undefined;
  handleSubmit: () => void;
  onDismiss: () => void;
  refetch: () => void;
}
import UNSTAMP_TRANSACTION from '../../../../../../../../ap/signing/accountingEntry/view/InvoiceSigningAccountingUnStamper.graphql';
import INVOICE_SIGNING_APPROVAL_REVOKE from '../../../../../../../../common/components/Modules/TransactionEdit/graphql/InvoiceSigningApprovalRevoke.graphql';
import INVOICES from '../../../../../../../../common/components/Modules/TransactionEdit/graphql/InvoicesSigningSearch.graphql';
import INVOICE_BATCHES from '../../../../../../../batchEdit/unpostedTransactions/InvoiceBatchModal/InvoiceBatches.graphql';
import BATCH_TRANSACTION from '../../../../BatchTransaction.graphql';

export const FooterActions: React.FC<FooterActionsProps> = ({
  handleSubmit,
  invoiceDetailsData,
  refetch,
  batchTransactionId,
  onDismiss,
}) => {
  const styles = useStyles();

  const [hideConfirmAmendDialog, { toggle: toggleConfirmAmendDialog }] =
    useBoolean(true);
  const [hideConfirmRemoveStampdDialog, { toggle: toggleRemoveStampdDialog }] =
    useBoolean(true);
  const {
    formState: { isDirty },
  } = useFormContext<AccountingEntryValues>();
  const [unStampTransaction] = useMutation<
    InvoiceSigningAccountingUnStamper,
    InvoiceSigningAccountingUnStamperVariables
  >(UNSTAMP_TRANSACTION, { errorPolicy: 'all' });
  const [invoiceSigningApprovalRevoke] = useMutation<
    InvoiceSigningApprovalRevoke,
    InvoiceSigningApprovalRevokeVariables
  >(INVOICE_SIGNING_APPROVAL_REVOKE, { errorPolicy: 'all' });

  const CONFIRM_STAMP_REMOVE_DIALOG_TITLE = 'Are you sure?';
  const CONFIRM_STAMP_REMOVE_DIALOG_SUBTEXT =
    'This will remove the stamp and remove the transaction from the batch.';
  const CONFIRM_AMEND_DIALOG_TITLE = 'Are you sure?';
  const CONFIRM_AMEND_DIALOG_SUBTEXT =
    'This will remove the Transaction signing from the approval cycle and require re-approval.';
  return (
    <Stack className={styles.container}>
      <Stack
        horizontal
        horizontalAlign="end"
        tokens={{ childrenGap: 20, padding: 20 }}
      >
        <PrimaryButton
          className={styles.diabledButton}
          text="Save"
          onClick={handleSubmit}
          disabled={!isDirty}
        />
        <PrimaryButton
          className={styles.diabledButton}
          text="Cancel"
          onClick={onDismiss}
        />
        {invoiceDetailsData?.invoice?._isApprovalRevocable && (
          <PrimaryButton
            className={styles.diabledButton}
            text="Amend Transaction"
            onClick={() => toggleConfirmAmendDialog()}
          />
        )}
        {invoiceDetailsData?.invoice?._isAccountingEntryStampedComplete && (
          <PrimaryButton
            className={styles.diabledButton}
            text="Remove Stamp"
            onClick={() => toggleRemoveStampdDialog()}
          />
        )}
        <DialogWrapper
          hidden={hideConfirmRemoveStampdDialog}
          onDismiss={toggleRemoveStampdDialog}
          dialogContentProps={{
            title: CONFIRM_STAMP_REMOVE_DIALOG_TITLE,
            type: DialogType.largeHeader,
            subText: CONFIRM_STAMP_REMOVE_DIALOG_SUBTEXT,
          }}
        >
          <DialogFooter>
            <PrimaryButton
              text="Unstamp"
              className={styles.unstampButton}
              onClick={async () => {
                toggleRemoveStampdDialog();
                const inputVariables: InvoiceSigningAccountingUnstamperInput = {
                  batchTransactionId: batchTransactionId!,
                  id: invoiceDetailsData?.invoice?.id!,
                  rowTimestamp: invoiceDetailsData?.invoice?._rowTimestamp!,
                };

                const { errors } = await unStampTransaction({
                  variables: {
                    input: inputVariables,
                  },
                  awaitRefetchQueries: true,
                  refetchQueries: [
                    {
                      query: INVOICE_BATCHES,
                      variables: {
                        id: batchTransactionId,
                        orderBy: [
                          '_ACCOUNTING_STAMP_TRANSACTION_REFERENCE_ASC',
                          'PRIMARY_KEY_ASC',
                        ],
                      },
                    },
                    {
                      query: BATCH_TRANSACTION,
                      variables: {
                        id: batchTransactionId,
                      },
                    },
                  ],
                });
                if (errors?.length)
                  showCustomToast({
                    content: errors[0].message,
                    appearance: 'error',
                  });
                else {
                  refetch?.();
                  showCustomToast({
                    content: 'Unstamp successful',
                    appearance: 'success',
                  });
                }
              }}
            />
            <DefaultButton onClick={toggleRemoveStampdDialog} text="Cancel" />
          </DialogFooter>
        </DialogWrapper>
        <ConfirmDialog
          isAmendButton
          hidden={hideConfirmAmendDialog}
          title={CONFIRM_AMEND_DIALOG_TITLE}
          subText={CONFIRM_AMEND_DIALOG_SUBTEXT}
          onDismiss={toggleConfirmAmendDialog}
          onConfirm={async () => {
            toggleConfirmAmendDialog();
            const inputVariables: InvoiceSigningApprovalRevokeInput = {
              entityId: invoiceDetailsData?.invoice?.id!,
              rowTimestamp: invoiceDetailsData?.invoice?._rowTimestamp!,
            };
            const { errors } = await invoiceSigningApprovalRevoke({
              variables: {
                input: inputVariables,
              },
              awaitRefetchQueries: true,
              refetchQueries: [
                {
                  query: INVOICES,
                  variables: {
                    isSigningSearch: false,
                    isHistorySearch: false,
                    first: TABLE_ROWS,
                    orderBy: ['_CREATED_DATE_DESC', 'PRIMARY_KEY_ASC'],
                  },
                },
              ],
            });
            if (errors?.length)
              showCustomToast({
                content: errors[0].message,
                appearance: 'error',
              });
            else {
              onDismiss();
              showCustomToast({
                content: 'Approval amended successfully',
                appearance: 'success',
              });
            }
          }}
        />
      </Stack>
    </Stack>
  );
};
